import { useEffect, useRef, useState } from 'react'
import type { Frame } from '@praesto/shared'
import { useText } from '../i18n.js'
import type { Notes } from '../presentation/useNotes.js'

interface Props {
  frames: Frame[]
  activeId: string | null
  canEdit: boolean
  /**
   * Der Knopf zum Anlegen. Kommt fertig von außen, weil daran ein Aufklappmenü
   * hängt — die Liste soll nicht wissen müssen, welche Formate es gibt.
   */
  addControl: React.ReactNode
  onSelect: (frame: Frame) => void
  onRename: (id: string, name: string) => void
  onMove: (id: string, delta: number) => void
  /** Umsortieren per Ziehen: `id` landet an der Stelle von `beforeId`. */
  onReorder: (id: string, beforeId: string) => void
  onRemove: (id: string) => void
  /** Sprechernotizen; fehlen bei Gästen — die haben keine. */
  notes?: Notes
}

/**
 * Liste der Frames — zugleich die Präsentationsreihenfolge.
 *
 * Umsortiert wird durch Ziehen, wie in der Ebenenliste. Vorher standen dafür
 * ↑/↓ an jeder Zeile; die kosteten aber dauerhaft Platz in einer schmalen
 * Liste, in der die Namen ohnehin schon abgeschnitten wurden. Über die Tastatur
 * geht es weiter: Alt+↑/↓ auf der Zeile verschiebt sie ohne Maus.
 */
export function FramesPanel({
  frames,
  activeId,
  canEdit,
  addControl,
  onSelect,
  onRename,
  onMove,
  onReorder,
  onRemove,
  notes,
}: Props): React.ReactElement {
  const t = useText()
  const [open, setOpen] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [notesOpenId, setNotesOpenId] = useState<string | null>(null)
  /** Vor welcher Zeile die gezogene landen würde — als Marke in der Liste. */
  const [dropBefore, setDropBefore] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editingId) inputRef.current?.select()
  }, [editingId])

  function commit(): void {
    if (editingId && draft.trim()) onRename(editingId, draft.trim())
    setEditingId(null)
  }

  return (
    <aside className={`frames-panel${open ? '' : ' is-collapsed'}`}>
      <header className="frames-head">
        <button
          className="button ghost frames-toggle"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <span className={`chevron${open ? ' is-open' : ''}`}>›</span>
          Frames
          {frames.length > 0 && <span className="frames-count">{frames.length}</span>}
        </button>
        {canEdit && open && addControl}
      </header>

      {open && (
        <>
          {frames.length === 0 && (
            <p className="frames-empty">{t('frames.empty')}</p>
          )}

          <ol className="frames-list">
            {frames.map((frame, index) => (
              <li
                key={frame.id}
                className={[
                  frame.id === activeId ? 'is-active' : '',
                  dropBefore === frame.id ? 'is-drop-before' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                // Nur mit Schreibrecht und nicht während des Umbenennens: Sonst
                // zöge man beim Markieren von Text die ganze Zeile mit.
                draggable={canEdit && editingId !== frame.id}
                onDragStart={(event) => {
                  event.dataTransfer.setData('text/praesto-frame', frame.id)
                  event.dataTransfer.effectAllowed = 'move'
                }}
                onDragEnd={() => setDropBefore(null)}
                onDragOver={(event) => {
                  if (!event.dataTransfer.types.includes('text/praesto-frame')) return
                  event.preventDefault()
                  setDropBefore(frame.id)
                }}
                onDragLeave={() =>
                  setDropBefore((current) => (current === frame.id ? null : current))
                }
                onDrop={(event) => {
                  event.preventDefault()
                  setDropBefore(null)
                  const gezogen = event.dataTransfer.getData('text/praesto-frame')
                  if (gezogen && gezogen !== frame.id) onReorder(gezogen, frame.id)
                }}
                onKeyDown={(event) => {
                  // Der Weg ohne Maus — und der einzige, seit ↑/↓ nicht mehr an
                  // jeder Zeile stehen.
                  if (!canEdit || !event.altKey) return
                  if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    onMove(frame.id, -1)
                  }
                  if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    onMove(frame.id, 1)
                  }
                }}
              >
                <span className="frames-grip" title={t('frames.drag')}>
                  ⠿
                </span>
                <span className="frames-order">{index + 1}</span>

                {editingId === frame.id ? (
                  <input
                    ref={inputRef}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={commit}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') commit()
                      if (event.key === 'Escape') setEditingId(null)
                    }}
                  />
                ) : (
                  <button
                    className="frames-name"
                    onClick={() => onSelect(frame)}
                    onDoubleClick={() => {
                      if (!canEdit) return
                      setDraft(frame.name)
                      setEditingId(frame.id)
                    }}
                    /* Der volle Name gehört in den Tooltip: In der schmalen
                       Liste ist er oft abgeschnitten. */
                    title={`${frame.name}\n${t('frames.itemTitle')}`}
                  >
                    {frame.name}
                  </button>
                )}

                {canEdit && (
                  <span className="frames-actions">
                    {notes && (
                      <button
                        className={`button ghost icon${
                          notesOpenId === frame.id ? ' is-on' : notes.has(frame.id) ? ' has-note' : ''
                        }`}
                        onClick={() =>
                          setNotesOpenId((current) => (current === frame.id ? null : frame.id))
                        }
                        title={t('frames.noteTitle')}
                        aria-label={t('frames.note')}
                        aria-expanded={notesOpenId === frame.id}
                      >
                        ✎
                      </button>
                    )}
                    <button
                      className="button ghost icon danger"
                      onClick={() => onRemove(frame.id)}
                      aria-label={t('frames.delete')}
                    >
                      ×
                    </button>
                  </span>
                )}

                {/* Die Notiz steht unter dem Frame, zu dem sie gehört — nicht in
                    einem eigenen Fenster: Wer sie schreibt, will die Reihenfolge
                    daneben sehen. */}
                {notes && notesOpenId === frame.id && (
                  <div className="frames-note">
                    <textarea
                      autoFocus
                      value={notes.get(frame.id)}
                      onChange={(event) => notes.set(frame.id, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setNotesOpenId(null)
                      }}
                      placeholder={t('frames.notePlaceholder')}
                      rows={4}
                    />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </aside>
  )
}
